import type {
  OfferId, RankResult, RunMode, WeaponId,
} from '../core/types';
import type { Engine } from '../game/engine';
import { OFFER_META } from '../game/offer-meta';
import { pickTitle } from '../game/titles';
import type { RunSummary } from '../net/score';
import { buildReport } from '../net/report';

/** 结算页榜位视图（离线快照语义）。 */
export type RankView =
  | { kind: 'practice' }
  | { kind: 'pending' }
  | { kind: 'rank'; r: RankResult }
  | { kind: 'unranked' }
  | { kind: 'done' }
  | { kind: 'error'; msg: string };

interface ScreenEls {
  start: HTMLElement;
  startStatus: HTMLElement;
  startRecovery: HTMLElement;
  levelup: HTMLElement;
  cards: HTMLElement;
  dead: HTMLElement;
  deadCause: HTMLElement;
  deadTitle: HTMLElement;
  deadTime: HTMLElement;
  deadKills: HTMLElement;
  deadLevel: HTMLElement;
  deadScore: HTMLElement;
  deadReport: HTMLElement;
  deadRank: HTMLElement;
  complete: HTMLElement;
  completeTitle: HTMLElement;
  completeNote: HTMLElement;
  completeTitleline: HTMLElement;
  completeKills: HTMLElement;
  completeLevel: HTMLElement;
  completeScore: HTMLElement;
  completeReport: HTMLElement;
  completeRank: HTMLElement;
  pause: HTMLElement;
}

const MODE_LABEL: Record<RunMode, string> = {
  daily: '日课', race: '日赛', practice: '练习',
};

const RANK_TEXT: Record<RankView['kind'], string> = {
  practice: '练习模式 · 不进榜',
  pending: '成绩已提交，位次稍后更新（约 5–10 分钟）',
  unranked: '快照中暂无本场位次，稍后更新',
  done: '今日已打过，成绩以首次结算为准',
  rank: '',
  error: '成绩提交失败，本场未进榜',
};

export class Screens {
  /** 当前两个结算屏的战报文本（供复制按钮）。 */
  reportText = '';
  summary: RunSummary | null = null;

  constructor(private els: ScreenEls) {}

  showStart(): void {
    this.els.start.classList.remove('hidden');
  }

  hideStart(): void {
    this.els.start.classList.add('hidden');
  }

  setStartStatus(text: string, ok: boolean): void {
    this.els.startStatus.innerHTML = text.replace(/\n/g, '<br />');
    this.els.startStatus.style.color = ok ? '' : 'var(--hp)';
  }

  setRecovery(text: string): void {
    this.els.startRecovery.textContent = text;
  }

  showLevelUp(engine: Engine, onPick: (id: OfferId) => void): void {
    const cards = this.els.cards;
    cards.textContent = '';
    engine.offers.forEach((id, i) => {
      const meta = OFFER_META[id];
      const isNew = meta.kind === 'weapon' && engine.build.weapon[id as WeaponId] === 0;
      const tag = isNew ? '新武器' : meta.kind === 'weapon' ? '武器升级' : '被动';
      const btn = document.createElement('button');
      btn.className = 'upgrade-card';
      btn.innerHTML =
        `<span class="upgrade-key">${i + 1}</span>` +
        `<span class="upgrade-tag">${tag}</span>` +
        `<span class="upgrade-name">${meta.name}</span>` +
        `<span class="upgrade-desc">${meta.desc}</span>`;
      btn.addEventListener('click', () => onPick(id));
      cards.appendChild(btn);
    });
    this.els.levelup.classList.remove('hidden');
  }

  hideLevelUp(): void {
    this.els.levelup.classList.add('hidden');
  }

  showDead(engine: Engine, view: RankView): void {
    this.els.deadCause.textContent = engine.death ? engine.death.label : '未知';
    this.fillSettlement(engine, false, this.els.dead, view);
  }

  hideDead(): void {
    this.els.dead.classList.add('hidden');
  }

  showComplete(engine: Engine, mode: RunMode, view: RankView): void {
    this.els.completeTitle.textContent = `${MODE_LABEL[mode]}完成`;
    const killed =
      mode === 'race' ? engine.boss2KilledAt !== null
      : engine.boss1KilledAt !== null;
    this.els.completeNote.textContent = killed
      ? 'Boss 已击杀，胜利结算'
      : '存活到终场';
    this.fillSettlement(engine, true, this.els.complete, view);
  }

  hideComplete(): void {
    this.els.complete.classList.add('hidden');
  }

  showPause(): void {
    this.els.pause.classList.remove('hidden');
  }

  hidePause(): void {
    this.els.pause.classList.add('hidden');
  }

  private evolutionCount(engine: Engine): number {
    let n = 0;
    for (const v of Object.values(engine.build.evolved)) if (v) n++;
    return n;
  }

  /** 两个结算屏的公共填充。 */
  private fillSettlement(
    engine: Engine, complete: boolean, panel: HTMLElement, view: RankView,
  ): void {
    const p = engine.player;
    const cause = complete
      ? (engine.boss1KilledAt !== null || engine.boss2KilledAt !== null
        ? 'VICTORY' : 'TIMEOUT')
      : engine.death?.cause ?? 'ATTRITION';
    const summary: RunSummary = {
      date: engine.date,
      mode: engine.mode === 'practice' ? 'daily' : engine.mode,
      score: engine.scoreBreakdown?.total ?? 0,
      kills: engine.kills,
      time: engine.time,
      level: p.level,
      evolutions: this.evolutionCount(engine),
      cause,
    };
    this.summary = summary;
    const rankResult = view.kind === 'rank' ? view.r : null;
    this.reportText = buildReport(summary, rankResult);
    let passiveCoverage = 0;
    for (const v of Object.values(engine.build.passive)) if (v > 0) passiveCoverage++;
    const title = complete ? '日抛冠军' : pickTitle({
      cause, time: Math.floor(engine.time), kills: engine.kills,
      level: p.level, evolutions: summary.evolutions,
      passiveCoverage, gold: engine.gold,
    });
    this.els.deadTitle.textContent = complete ? '' : `称号 · ${title}`;
    this.els.completeTitleline.textContent = complete
      ? `称号 · ${title}` : '';

    const set = (el: HTMLElement, text: string): void => {
      el.textContent = text;
    };
    if (complete) {
      set(this.els.completeKills, String(engine.kills));
      set(this.els.completeLevel, String(p.level));
      set(this.els.completeScore, String(summary.score));
      set(this.els.completeReport, this.reportText);
      this.setRankLine(this.els.completeRank, view);
    } else {
      set(this.els.deadTime, this.fmt(engine.time));
      set(this.els.deadKills, String(engine.kills));
      set(this.els.deadLevel, String(p.level));
      set(this.els.deadScore, String(summary.score));
      set(this.els.deadReport, this.reportText);
      this.setRankLine(this.els.deadRank, view);
    }
    panel.classList.remove('hidden');
  }

  private setRankLine(el: HTMLElement, view: RankView): void {
    if (view.kind === 'rank') {
      const r = view.r;
      el.textContent =
        `🏅 榜位 #${r.rank} / ${r.total} · 击败今日 ${r.beatenPct}%` +
        `（快照 ${r.generatedAt.replace('T', ' ').slice(5, 16)}）`;
      el.classList.remove('dim');
    } else {
      el.textContent = RANK_TEXT[view.kind];
      el.classList.toggle('dim', view.kind !== 'error');
    }
  }

  private fmt(t: number): string {
    const s = Math.round(t);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }
}
